/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, CSSProperties, ReactElement } from 'react';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import { CodeBlockSharedCssClassName } from '@atlaskit/editor-common/styles/code-block';
import type { CodeBlock as OriginalCodeBlock } from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

const styles = cssMap({
	codeBlock: {
		backgroundColor: token('elevation.surface.raised'),
		boxSizing: 'border-box',
		minWidth: '48px',
	},
	scroller: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		boxSizing: 'border-box',
		direction: 'ltr',
		display: 'block',
		font: token('font.code'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Matches the Platform Renderer dense code-block geometry.
		fontSize: '0.875em',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Matches the Platform Renderer dense code-block geometry.
		lineHeight: '1.5em',
		margin: token('space.0'),
		minHeight: '2.5rem',
		overflowX: 'auto',
		overflowY: 'hidden',
		position: 'relative',
		width: '100%',
	},
	code: {
		boxSizing: 'border-box',
		color: token('color.text'),
		display: 'block',
		font: 'inherit',
		minWidth: 0,
		paddingBlock: token('space.100'),
		paddingInlineStart: 'var(--light-code-line-number-gutter, 0px)',
		tabSize: 4,
		whiteSpace: 'pre',
	},
	wrapped: {
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
});

const getCodeLineNumberGutterWidth = (text: string): string => {
	let lineCount = 1;
	let newlineIndex = text.indexOf('\n');

	while (newlineIndex !== -1) {
		lineCount += 1;
		newlineIndex = text.indexOf('\n', newlineIndex + 1);
	}

	return `calc(${String(lineCount).length}ch + 24px)`;
};

export const CodeBlock = (
	props: ComponentProps<typeof OriginalCodeBlock> & LightNodeProps,
): ReactElement => {
	const shouldWrap = Boolean(props.allowWrapCodeBlock && props.wrap);
	const lineNumberGutterWidth = props.hideLineNumbers
		? '0px'
		: getCodeLineNumberGutterWidth(props.text);

	return (
		<div
			ref={props.lightNodeRef}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Preserve Platform's lightweight code-block style contract.
			className={`light-weight-code-block ${props.className ?? ''}`}
			data-language={props.language}
			data-local-id={props.localId}
		>
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Preserve Platform's code-block style hook.
				className={CodeBlockSharedCssClassName.CODEBLOCK_CONTAINER}
				css={styles.codeBlock}
			>
				<pre
					data-ds--code--code-block=""
					css={styles.scroller}
					style={
						{
							'--light-code-line-number-gutter': lineNumberGutterWidth,
						} as CSSProperties
					}
				>
					{/* eslint-disable-next-line @atlaskit/design-system/no-html-code -- A native code element is the inert semantic subtree; the ADS component would add unnecessary rendering behavior. */}
					<code css={[styles.code, shouldWrap && styles.wrapped]}>{props.text}</code>
				</pre>
			</div>
		</div>
	);
};
